import { SimpleGrid } from '@chakra-ui/react';
import useConfig from 'data/useConfig';
import { useFormikContext } from 'formik';
import { RuianData } from 'generated';
import useTranslations from 'i18n';
import { identity } from 'lodash';
import { useCallback, useMemo } from 'react';
import { ConfigField } from 'types/config';
import maskString from 'utils/maskString';
import AddressAutocompleteControl from './AddressAutocompleteControl';
import InputControl from './InputControl';

interface AddressFieldProps {
  streetName: string;
  houseNumber: string;
  box?: string;
  city: string;
  postalCode: string;
}

interface AddressControlProps {
  config: {
    streetName: ConfigField;
    houseNumber: ConfigField;
    box?: ConfigField;
    city: ConfigField;
    postalCode: ConfigField;
  };
  disabled?: boolean;
  idPrefix: string;
  readonly?: boolean;
  tPrefix: string;
  withSuggestion?: boolean;
}

const AddressControl = ({
  config,
  disabled,
  idPrefix,
  readonly,
  tPrefix,
  withSuggestion = false,
}: AddressControlProps) => {
  const { t } = useTranslations();
  const { setValues } = useFormikContext<AddressFieldProps>();
  const { addressAutocomplete: isAddressSuggestionEnabled } = useConfig();
  const showSuggestion = isAddressSuggestionEnabled && withSuggestion && !readonly;

  const handleSetAddress = useCallback(
    (op?: RuianData) => {
      setValues(
        (prevValues) => ({
          ...prevValues,
          streetName: op?.street || op?.city || '',
          ...(config.houseNumber.visible
            ? {
                houseNumber: [op?.houseNumber || '', op?.houseOrientationNumber || '']
                  .filter(identity)
                  .join('/'),
              }
            : {}),
          ...(config.city.visible ? { city: op?.city || '' } : {}),
          ...(config.postalCode.visible
            ? { postalCode: maskString(op?.zip || '', config.postalCode.format) }
            : {}),
        }),
        true,
      );
    },
    [config, setValues],
  );

  const streetColumns = useMemo(
    () =>
      [config.streetName.visible, config.houseNumber.visible, config.box?.visible].filter(identity)
        .length,
    [config.box?.visible, config.houseNumber.visible, config.streetName.visible],
  );

  return (
    <>
      {streetColumns > 0 && (
        <SimpleGrid columns={{ base: 1, md: streetColumns }} spacing={4}>
          {config.streetName.visible &&
            (showSuggestion ? (
              <AddressAutocompleteControl
                readonly={readonly}
                disabled={disabled}
                mandatory={config.streetName.mandatory}
                name="streetName"
                id={`${idPrefix}_streetName`}
                placeholder={t(`${tPrefix}.streetName.label`, 'Street name')}
                title={t(`${tPrefix}.streetName.label`, 'Street name')}
                onClear={handleSetAddress}
                onOptionSelect={handleSetAddress}
                loadingText={t('global_texts.labels.loading_data', 'Loading...')}
                notFoundText={t('global_texts.labels.noResults', 'No results matching a query')}
              />
            ) : (
              <InputControl
                readonly={readonly}
                disabled={disabled}
                type="text"
                mandatory={config.streetName.mandatory}
                name="streetName"
                id={`${idPrefix}_streetName`}
                placeholder={t(`${tPrefix}.streetName.label`, 'Street name')}
                title={t(`${tPrefix}.streetName.label`, 'Street name')}
              />
            ))}
          {config.houseNumber.visible && (
            <InputControl
              readonly={readonly}
              disabled={disabled}
              type="text"
              mandatory={config.houseNumber.mandatory}
              name="houseNumber"
              id={`${idPrefix}_houseNumber`}
              placeholder={t(`${tPrefix}.houseNumber.label`, 'House number')}
              title={t(`${tPrefix}.houseNumber.label`, 'House number')}
            />
          )}
          {config.box?.visible && (
            <InputControl
              readonly={readonly}
              disabled={disabled}
              type="text"
              mandatory={config.box?.mandatory}
              name="box"
              id={`${idPrefix}_box`}
              placeholder={t(`${tPrefix}.box.label`, 'Box')}
              title={t(`${tPrefix}.box.label`, 'Box')}
            />
          )}
        </SimpleGrid>
      )}
      {(config.city.visible || config.postalCode.visible) && (
        <SimpleGrid
          columns={{ base: 1, md: config.city.visible && config.postalCode.visible ? 2 : 1 }}
          spacing={4}
        >
          {config.city.visible && (
            <InputControl
              readonly={readonly}
              disabled={disabled}
              type="text"
              mandatory={config.city.mandatory}
              name="city"
              id={`${idPrefix}_city`}
              placeholder={t(`${tPrefix}.city.label`, 'City')}
              title={t(`${tPrefix}.city.label`, 'City')}
            />
          )}
          {config.postalCode.visible && (
            <InputControl
              readonly={readonly}
              disabled={disabled}
              type="text"
              mandatory={config.postalCode.mandatory}
              name="postalCode"
              id={`${idPrefix}_postalCode`}
              placeholder={t(`${tPrefix}.postalCode.label`, 'ZIP Code')}
              title={t(`${tPrefix}.postalCode.label`, 'ZIP Code')}
            />
          )}
        </SimpleGrid>
      )}
    </>
  );
};

export default AddressControl;
